import React, { useEffect, useState } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

am4core.useTheme(am4themes_animated);

/* =========================
   STYLES
========================= */



const LINE_COLORS = [
    "#ff6b6b",
    "#7aa2ff",
    "#4dd0e1",
    "#b18cff",
    "#f59e0b",
    "#22c55e",
    "#ec4899",
    "#0ea5e9",
    "#a855f7",
    "#84cc16"
];

const drawer = {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    boxSizing: "border-box",
    background: "#ffffff",
    display: "flex",
    flexDirection: "column",
    padding: "24px 26px"
};

const header = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "14px"
};

const title = {
    fontSize: "18px",
    fontWeight: 700
};

const closeBtn = {
    cursor: "pointer",
    fontSize: "22px",
    opacity: 0.5
};

/* KPI STRIP */

const kpiStrip = {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "12px",
    marginBottom: "18px"
};

const kpiBox = (bg, color) => ({
    background: bg,
    borderRadius: "14px",
    padding: "14px",
    color: 'white',
    boxShadow: "0 8px 20px rgba(0,0,0,0.12)"
});

const kpiLabel = {
    fontSize: "11px",
    textTransform: "uppercase",
    opacity: 0.75,
    marginBottom: "4px"
};

const kpiValue = {
    fontSize: "20px",
    fontWeight: 700
};

const kpiSub = {
    fontSize: "12px",
    opacity: 0.8
};

/* MAIN CONTENT */

const content = {
    flex: 1,
    display: "flex",
    gap: "22px",
    overflow: "hidden"
};

const leftCol = {
    width: "38%",
    display: "flex",
    flexDirection: "column",
    gap: "18px"
};

const rightCol = {
    flex: 1,
    display: "flex",
    flexDirection: "column"
};

const sectionTitle = {
    fontSize: "12px",
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    color: "#666",
    marginBottom: "6px"
};

const chartBlock = {
  flex: 1,
  height: "200px",
  borderRadius: "14px",
  background: "linear-gradient(180deg, #f6f7f9 0%, #eef0f3 100%)",
  border: "1px solid rgba(0,0,0,0.05)",
  boxShadow: `
    0 1px 0 rgba(255,255,255,0.9) inset,
    0 6px 16px rgba(0,0,0,0.06)
  `
};

/* TOGGLE */

const toggleWrap = {
    display: "flex",
    justifyContent: "center",
    marginTop: "10px"
};

const toggle = {
    display: "flex",
    background: "#eee",
    borderRadius: "18px",
    padding: "3px"
};

const toggleBtn = (active) => ({
    padding: "6px 18px",
    fontSize: "12px",
    borderRadius: "14px",
    cursor: "pointer",
    background: active ? "#111" : "transparent",
    color: active ? "#fff" : "#555"
});

/* =========================
   DUMMY DATA (10 ITEMS)
========================= */

const worst = Array.from({ length: 10 }, (_, i) => ({
    name: `Building ${String.fromCharCode(65 + i)}`,
    value: 50 - i * 3
}));

const best = Array.from({ length: 10 }, (_, i) => ({
    name: `Building ${String.fromCharCode(75 + i)}`,
    value: -10 - i * 2
}));

const trendData = Array.from({ length: 12 }, (_, m) => {
    const row = { month: `2024-${m + 1}` };
    [...worst, ...best].slice(0, 10).forEach((b, i) => {
        row[b.name] = Math.round(Math.random() * 30 + i * 4);
    });
    return row;
});

/* =========================
   COMPONENT
========================= */

export default function WhiteMetricDrawer({ metric, onClose }) {

    const [mode, setMode] = useState("worst");

    useEffect(() => {

        /* ===== BAR CHART FACTORY ===== */
        function buildBar(div, data, color) {
            let chart = am4core.create(div, am4charts.XYChart);
            chart.data = data;

            let cat = chart.yAxes.push(new am4charts.CategoryAxis());
            cat.dataFields.category = "name";
            cat.renderer.inversed = true;
            cat.renderer.grid.template.disabled = true;
            cat.renderer.labels.template.disabled = false;
            cat.renderer.labels.template.fontSize = 12;
			
			
			cat.renderer.minGridDistance = 1;
			cat.renderer.labels.template.truncate = false;
			cat.renderer.labels.template.wrap = true;
			cat.renderer.labels.template.fontSize = 11;

            let val = chart.xAxes.push(new am4charts.ValueAxis());
            val.renderer.grid.template.strokeOpacity = 0.1;

            let series = chart.series.push(new am4charts.ColumnSeries());
            series.dataFields.valueX = "value";
            series.dataFields.categoryY = "name";
            series.columns.template.fill = am4core.color(color);
            series.columns.template.strokeOpacity = 0;
            series.tooltipText = "{categoryY}: {valueX}%";

            return chart;
        }

        const worstChart = buildBar("worstChart", worst, "#990000");
        const bestChart = buildBar("bestChart", best, "#009900");

        /* ===== TREND ===== */
        let trendChart = am4core.create("trendChart", am4charts.XYChart);
        trendChart.data = trendData;

        let x = trendChart.xAxes.push(new am4charts.CategoryAxis());
        x.dataFields.category = "month";

        let y = trendChart.yAxes.push(new am4charts.ValueAxis());

        const source = mode === "worst" ? worst : best;

        source.forEach((b, i) => {
            let s = trendChart.series.push(new am4charts.LineSeries());
            s.dataFields.valueY = b.name;
            s.dataFields.categoryX = "month";
            s.name = b.name;
            s.strokeWidth = 2;
            s.tensionX = 0.8;
            s.stroke = am4core.color(LINE_COLORS[i % LINE_COLORS.length]);
        });

        trendChart.legend = new am4charts.Legend();
        trendChart.cursor = new am4charts.XYCursor();

        return () => {
            worstChart.dispose();
            bestChart.dispose();
            trendChart.dispose();
        };

    }, [metric, mode]);
	
	const lighten = (hex, amt) =>
	    `hsl(${hexToHsl(hex).h}, ${hexToHsl(hex).s}%, ${Math.min(hexToHsl(hex).l + amt * 100, 90)}%)`;

	const darken = (hex, amt) =>
	    `hsl(${hexToHsl(hex).h}, ${hexToHsl(hex).s}%, ${Math.max(hexToHsl(hex).l - amt * 100, 10)}%)`;

	const hexToHsl = (hex) => {
	    let r = parseInt(hex.slice(1,3),16)/255;
	    let g = parseInt(hex.slice(3,5),16)/255;
	    let b = parseInt(hex.slice(5,7),16)/255;

	    const max = Math.max(r,g,b), min = Math.min(r,g,b);
	    let h, s, l = (max + min) / 2;

	    if (max === min) {
	        h = s = 0;
	    } else {
	        const d = max - min;
	        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
	        switch (max) {
	            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
	            case g: h = (b - r) / d + 2; break;
	            case b: h = (r - g) / d + 4; break;
	            default: break;
	        }
	        h *= 60;
	    }

	    return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
	};
	
	const kpiBox = (base) => ({
	    background: `linear-gradient(
	        135deg,
	        ${lighten(base, 0.05)} 0%,
	        ${base} 50%,
	        ${darken(base, 0.05)} 100%
	    )`,
	    borderRadius: "14px",
	    padding: "16px 18px",
	    color: "#fff",
	    boxShadow: "0 10px 26px rgba(0,0,0,0.22)",
	    display: "flex",
	    flexDirection: "column",
	    gap: "6px"
	});

    return (
        <div style={drawer}>

            {/* HEADER */}
            <div style={header}>
                <div style={title}>{metric.label || metric}</div>
                <div style={closeBtn} onClick={onClose}>×</div>
            </div>

            {/* KPI STRIP */}
			<div style={kpiStrip}>
			    <div style={kpiBox("#990000")}>
			        <div style={kpiLabel}>Worst Value</div>
			        <div style={kpiValue}>+52%</div>
			        <div style={kpiSub}>June 2025</div>
			    </div>

			    <div style={kpiBox("#990000")}>
			        <div style={kpiLabel}>Largest Increase</div>
			        <div style={kpiValue}>+18%</div>
			        <div style={kpiSub}>Last period</div>
			    </div>

			    <div style={kpiBox("#009900")}>
			        <div style={kpiLabel}>Best Value</div>
			        <div style={kpiValue}>−28%</div>
			        <div style={kpiSub}>May 2025</div>
			    </div>

			    <div style={kpiBox("#009900")}>
			        <div style={kpiLabel}>Largest Reduction</div>
			        <div style={kpiValue}>−14%</div>
			        <div style={kpiSub}>Last period</div>
			    </div>
			</div>

            {/* CONTENT */}
            <div style={content}>

                <div style={leftCol}>
                    <div>
                        <div style={sectionTitle}>Worst 10</div>
                        <div id="worstChart" style={chartBlock} />
                    </div>
                    <div>
                        <div style={sectionTitle}>Best 10</div>
                        <div id="bestChart" style={chartBlock} />
                    </div>
                </div>

                <div style={rightCol}>
                    <div style={sectionTitle}>Trend over time</div>
                    <div id="trendChart" style={chartBlock} />

                    <div style={toggleWrap}>
                        <div style={toggle}>
                            <div
                                style={toggleBtn(mode === "worst")}
                                onClick={() => setMode("worst")}
                            >
                                Worst
                            </div>
                            <div
                                style={toggleBtn(mode === "best")}
                                onClick={() => setMode("best")}
                            >
                                Best
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}